<script lang="ts" setup generic="T extends BeamShapingGroupDto">
import { api } from '@/api';
import {
  BeamShapingGroupDto,
  EnumerationValueDto,
  GetBeamShapingGroupDtoTypeEnum,
  instanceOfBeamShapingGroupCircularDto,
  instanceOfBeamShapingGroupRectangularDto,
  TaskTypeDescription,
} from '@/api/generator';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';

import VeeEnumerationValue from '@/components/forms/dataTypes/VeeEnumerationValue.vue';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { I18nKey } from '@/utils/typings.ts';
import BeamShapingGroupCircular from '@/views/tasks/components/beamShaping/BeamShapingGroupCircular.vue';
import BeamShapingGroupRectangular from '@/views/tasks/components/beamShaping/BeamShapingGroupRectangular.vue';
import { useLatestApiRequest } from '@/views/jobOccurrences/utils/useLatestApiRequest';

import { useFieldValue, useSetFieldValue } from 'vee-validate';
import { toRef } from 'vue';

import { useTranslation } from 'i18next-vue';
const { t } = useTranslation();

const props = defineProps<{
  name: string; // taskItemArea[{areaIndex}].medicalSystemSetup.beamShapingContainer.selectedBeamShapings[{beamShapingGroupIndex}]
  taskType: TaskTypeDescription;
  canBeDeleted: boolean;
}>();

const emits = defineEmits<{
  remove: [item: BeamShapingGroupDto];
  blur: [event?: FocusEvent];
  switchShape: [];
}>();

const mode = useFormMode();

const item = useFieldValue<T>(toRef(() => props.name));

const setValue = useSetFieldValue<T>(toRef(() => props.name));
const beamShapingTemplateRequest = useLatestApiRequest({
  request: api.taskItemsApi.getTaskItemBeamShapingTemplate.bind(api.taskItemsApi),
});

async function switchBeamShape(shape: EnumerationValueDto | undefined) {
  if (shape?.value?.id == null) {
    return;
  }

  const { result } = await beamShapingTemplateRequest.call({
    getBeamShapingGroupDto: {
      taskType: props.taskType,
      radiationFieldShapeId: shape.value.id,
      $type:
        GetBeamShapingGroupDtoTypeEnum.PtwPtw100AppZeroApplicationSharedTaskItemComponentDtoGetBeamShapingGroupDtoPtwPtw100AppZeroApplicationShared,
    },
  });

  if (result) {
    setValue(result as T);
    emits('switchShape');
  }
}
</script>

<template>
  <tr class="v-data-table__tr">
    <td class="shape">
      <VeeEnumerationValue
        :name="`${name}.radiationFieldShape`"
        :items="
          item?.radiationFieldShape?.options?.map((option) => {
            return taskType === 'BeamConstancyCheck'
              ? {
                  title: `${t(option.name as I18nKey)}`,
                  value: option,
                  props: {
                    disabled: option.name !== ('EnumerationOptionLabelEnum.Rectangular' as I18nKey),
                  },
                }
              : {
                  title: `${t(option.name as I18nKey)}`,
                  value: option,
                };
          })
        "
        :mode="taskType === 'BeamConstancyCheck' ? 'details' : mode"
        @update:enumerationValue="switchBeamShape"
      />
    </td>

    <BeamShapingGroupRectangular
      v-if="instanceOfBeamShapingGroupRectangularDto(item)"
      :disabled="
        typeof item?.radiationFieldShape?.value === 'undefined' ||
        item?.radiationFieldSymmetry?.value?.name === 'EnumerationOptionLabelEnum.Symmetrical'
      "
      :name="name"
      :taskType="taskType"
      @blur="emits('blur', $event)"
    />

    <BeamShapingGroupCircular
      v-if="instanceOfBeamShapingGroupCircularDto(item)"
      :name="name"
      :taskType="taskType"
      @blur="emits('blur', $event)"
    />

    <td class="remove">
      <PtwDeleteButton v-if="mode !== 'details' && canBeDeleted" @delete="emits('remove', item)" />
    </td>
  </tr>
</template>

<style lang="scss" scoped>
.table-row {
  display: flex;
  justify-content: space-around;
}

tr {
  &.v-data-table__tr {
    outline: 2px solid transparent;
    outline-offset: -2px;
  }
}

td {
  &.shape {
    width: 15% !important;
  }

  &.remove {
    width: 56px !important;
    padding-left: 8px !important;
    vertical-align: middle !important;
  }
}
</style>
